import { supabase } from '../lib/supabase';
import auditLogService from './auditLogService';

const sessionManagementService = {
  /**
   * Create a new session entry in active_sessions table
   * @param {string} sessionToken - Supabase session access token
   * @param {object} deviceInfo - Device and browser information
   */
  async createSession(sessionToken, deviceInfo = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: null, error: { message: 'No user logged in' } };

      const sessionData = {
        user_id: user?.id,
        session_token: sessionToken,
        device_info: deviceInfo,
        browser: this.getBrowserInfo(),
        operating_system: this.getOSInfo(),
        ip_address: deviceInfo?.ipAddress || null,
        location: deviceInfo?.location || null,
        last_activity_at: new Date()?.toISOString(),
        expires_at: this.calculateExpiration(30), // Default 30 minutes
        is_current: true,
      };

      const { data, error } = await supabase?.from('active_sessions')?.insert(sessionData)?.select()?.single();
      
      // Log device addition
      if (data && !error) {
        await auditLogService?.createLog(
          user?.id,
          auditLogService?.EVENT_TYPES?.DEVICE_ADDED,
          `New device added: ${sessionData?.browser} on ${sessionData?.operating_system}`,
          {
            success: true,
            deviceInfo: { browser: sessionData?.browser, os: sessionData?.operating_system },
            ipAddress: sessionData?.ip_address,
            metadata: { session_id: data?.id }
          }
        );
      }
      
      return { data, error };
    } catch (error) {
      console.error('Error creating session:', error);
      return { data: null, error };
    }
  },

  /**
   * Update session activity timestamp
   * @param {string} sessionToken - Current session token
   */
  async updateActivity(sessionToken) {
    try {
      const { data, error } = await supabase?.rpc('update_session_activity', {
        p_session_token: sessionToken,
      });
      return { data, error };
    } catch (error) {
      console.error('Error updating session activity:', error);
      return { data: null, error };
    }
  },

  /**
   * Get all active sessions for current user
   */
  async getActiveSessions() {
    try {
      const { data, error } = await supabase?.from('active_sessions')?.select('*')?.order('last_activity_at', { ascending: false });
      return { data: data || [], error };
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      return { data: [], error };
    }
  },

  /**
   * Terminate a specific session
   * @param {string} sessionId - Session ID to terminate
   */
  async terminateSession(sessionId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      // Get session details before deletion for audit log
      const { data: sessionData } = await supabase?.from('active_sessions')?.select('*')?.eq('id', sessionId)?.single();
      
      const { error } = await supabase?.from('active_sessions')?.delete()?.eq('id', sessionId);
      
      // Log session termination
      if (!error && user && sessionData) {
        await auditLogService?.createLog(
          user?.id,
          auditLogService?.EVENT_TYPES?.SESSION_TERMINATED,
          `Session terminated: ${sessionData?.browser} on ${sessionData?.operating_system}`,
          {
            success: true,
            deviceInfo: { browser: sessionData?.browser, os: sessionData?.operating_system },
            metadata: { session_id: sessionId, terminated_by: 'user' }
          }
        );
      }
      
      return { error };
    } catch (error) {
      console.error('Error terminating session:', error);
      return { error };
    }
  },

  /**
   * Logout all devices except current
   * @param {string} currentSessionToken - Current session token to preserve
   */
  async logoutAllDevices(currentSessionToken) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase?.rpc('logout_all_devices', {
        p_current_session_token: currentSessionToken,
      });
      
      // Log logout all devices action
      if (!error && user) {
        await auditLogService?.createLog(
          user?.id,
          auditLogService?.EVENT_TYPES?.SESSION_TERMINATED,
          `Logged out from ${data || 0} other device(s)`,
          {
            success: true,
            metadata: { sessions_terminated: data || 0, action: 'logout_all_devices' }
          }
        );
      }
      
      return { data, error };
    } catch (error) {
      console.error('Error logging out all devices:', error);
      return { data: null, error };
    }
  },

  /**
   * Clean up expired sessions
   */
  async cleanupExpiredSessions() {
    try {
      const { data, error } = await supabase?.rpc('cleanup_expired_sessions');
      return { data, error };
    } catch (error) {
      console.error('Error cleaning up expired sessions:', error);
      return { data: null, error };
    }
  },

  /**
   * Get security preferences for current user
   * @param {string} userId - User ID
   */
  async getSecurityPreferences(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('security_preferences')?.eq('id', userId)?.single();
      if (error) throw error;
      return { data: data?.security_preferences || this.getDefaultSecurityPreferences(), error: null };
    } catch (error) {
      console.error('Error fetching security preferences:', error);
      return { data: this.getDefaultSecurityPreferences(), error };
    }
  },

  /**
   * Update security preferences
   * @param {string} userId - User ID
   * @param {object} preferences - Security preferences object
   */
  async updateSecurityPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ security_preferences: preferences })?.eq('id', userId)?.select()?.single();
      
      // Log security settings change
      if (!error && data) {
        await auditLogService?.createLog(
          userId,
          auditLogService?.EVENT_TYPES?.SECURITY_SETTINGS_CHANGE,
          'Security preferences updated',
          {
            success: true,
            metadata: {
              session_timeout_enabled: preferences?.sessionTimeout?.enabled,
              token_refresh_enabled: preferences?.tokenRefresh?.enabled
            }
          }
        );
      }
      
      return { data, error };
    } catch (error) {
      console.error('Error updating security preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Get default security preferences
   */
  getDefaultSecurityPreferences() {
    return {
      sessionTimeout: {
        enabled: true,
        duration: 30, // minutes
        autoLogout: true,
      },
      tokenRefresh: {
        enabled: true,
        interval: 15, // minutes
      },
    };
  },

  /**
   * Calculate session expiration time
   * @param {number} durationMinutes - Duration in minutes
   */
  calculateExpiration(durationMinutes) {
    const now = new Date();
    now?.setMinutes(now?.getMinutes() + durationMinutes);
    return now?.toISOString();
  },

  /**
   * Get browser information
   */
  getBrowserInfo() {
    const userAgent = navigator?.userAgent || '';
    if (userAgent?.includes('Chrome')) return 'Chrome';
    if (userAgent?.includes('Firefox')) return 'Firefox';
    if (userAgent?.includes('Safari')) return 'Safari';
    if (userAgent?.includes('Edge')) return 'Edge';
    return 'Unknown';
  },

  /**
   * Get operating system information
   */
  getOSInfo() {
    const userAgent = navigator?.userAgent || '';
    if (userAgent?.includes('Windows')) return 'Windows';
    if (userAgent?.includes('Mac')) return 'macOS';
    if (userAgent?.includes('Linux')) return 'Linux';
    if (userAgent?.includes('Android')) return 'Android';
    if (userAgent?.includes('iOS')) return 'iOS';
    return 'Unknown';
  },

  /**
   * Format last activity time
   * @param {string} timestamp - ISO timestamp
   */
  formatLastActivity(timestamp) {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  },
};

export default sessionManagementService;